import React, { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { validateAuthentication } from "@/utils/auth-middleware";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, Camera } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/features/authentication";

export interface PhotoData {
  id: string;
  file: File;
  preview: string;
  tags: string[];
}

export interface PhotoUploadGridProps {
  photos: PhotoData[];
  onPhotosChange: (photos: PhotoData[]) => void;
  maxPhotos?: number;
  availableTags?: string[];
  className?: string;
  disabled?: boolean;
}

export const PhotoUploadGrid: React.FC<PhotoUploadGridProps> = ({
  photos = [],
  onPhotosChange,
  maxPhotos = 5,
  availableTags = [
    "Sala privativa",
    "Copa da sala",
    "Banheiro privativo",
    "Sala de reunião da sala",
    "Armários da sala",
    "TV",
    "Projetor",
  ],
  className,
  disabled = false,
}) => {
  const { toast } = useToast();
  const { pathname } = useLocation();
  const { userInfo } = useAuth();
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    photoId: string | null;
  }>({
    isOpen: false,
    photoId: null,
  });

  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileUpload = useCallback(
    (files: FileList) => {
      const newFiles = Array.from(files);
      const availableSlots = maxPhotos - photos.length;
      const filesToAdd = newFiles.slice(0, availableSlots);

      const newPhotos: PhotoData[] = filesToAdd.map((file, index) => ({
        id: `photo-${Date.now()}-${index}`,
        file,
        preview: URL.createObjectURL(file),
        tags: [],
      }));

      onPhotosChange([...photos, ...newPhotos]);
    },
    [photos, onPhotosChange, maxPhotos],
  );

  const handleDeleteClick = useCallback((photoId: string) => {
    setDeleteConfirmation({
      isOpen: true,
      photoId: photoId,
    });
  }, []);

  const confirmDelete = useCallback(() => {
    if (deleteConfirmation.photoId) {
      const updatedPhotos = photos.filter(
        (photo) => photo.id !== deleteConfirmation.photoId,
      );
      onPhotosChange(updatedPhotos);

      // Show success toast
      toast({
        title: "Foto excluída com sucesso",
        description: "A foto foi removida da sua galeria.",
      });
    }
    setDeleteConfirmation({
      isOpen: false,
      photoId: null,
    });
  }, [photos, onPhotosChange, deleteConfirmation.photoId, toast]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirmation({
      isOpen: false,
      photoId: null,
    });
  }, []);

  const toggleTag = useCallback(
    (photoId: string, tag: string) => {
      const updatedPhotos = photos.map((photo) => {
        if (photo.id === photoId) {
          const currentTags = photo.tags || [];
          const hasTag = currentTags.includes(tag);

          const isExternalRoute = pathname.includes("/external/");
          const authResult = validateAuthentication(isExternalRoute);
          const isEdit = pathname.includes("/edit/");

          sendTrackDataServer({
            eventName: "PRIVATE_ROOM_PHOTO_TAG_SELECTED",
            data: {
              user_id:
                userInfo?.user?.id ||
                authResult.payload?.id ||
                authResult.payload?.userId ||
                "unknown",
              email:
                userInfo?.profile?.email ||
                authResult.payload?.email ||
                "unknown",
              flow_type: isEdit ? ("edit" as const) : ("create" as const),
              tag_label: tag,
            },
            url: pathname,
            productType: "PO",
          });

          return {
            ...photo,
            tags: hasTag
              ? currentTags.filter((t) => t !== tag)
              : [...currentTags, tag],
          };
        }
        return photo;
      });

      onPhotosChange(updatedPhotos);
    },
    [photos, onPhotosChange, pathname],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        // Filtrar apenas arquivos de imagem
        const imageFiles = Array.from(files).filter((file) =>
          file.type.startsWith("image/"),
        );

        if (imageFiles.length === 0) {
          toast({
            title: "Arquivo inválido",
            description:
              "Por favor, selecione apenas arquivos de imagem (JPEG, PNG, WEBP).",
            variant: "destructive",
          });
          return;
        }

        const fileList = new DataTransfer();
        imageFiles.forEach((file) => fileList.items.add(file));

        handleFileUpload(fileList.files);
      }
    },
    [disabled, handleFileUpload, toast],
  );

  const canAddMore = photos.length < maxPhotos;

  // Show large upload placeholder when no photos
  if (photos.length === 0) {
    return (
      <div className={cn("space-y-4", className)}>
        <div
          className={cn(
            "w-full border-2 border-dashed rounded-lg bg-gray-50 flex flex-col items-center justify-center cursor-pointer transition-colors group p-12",
            isDragOver
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 hover:bg-gray-100",
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => {
            if (!disabled) {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "image/jpeg,image/jpg,image/png,image/webp";
              input.multiple = true;
              input.onchange = (e) => {
                const target = e.target as HTMLInputElement;
                if (target.files) {
                  handleFileUpload(target.files);
                }
              };
              input.click();
            }
          }}
        >
          <div className="text-center space-y-4">
            <Camera className="mx-auto h-12 w-12 text-gray-400 group-hover:text-gray-600" />
            <div>
              <p className="text-gray-900 font-medium">
                Arraste suas fotos aqui ou clique para enviar
              </p>
              <p className="text-gray-500 text-sm mt-1">
                JPEG, PNG ou WEBP até 10MB
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              className="cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = "image/jpeg,image/jpg,image/png,image/webp";
                  input.multiple = true;
                  input.onchange = (e) => {
                    const target = e.target as HTMLInputElement;
                    if (target.files) {
                      handleFileUpload(target.files);
                    }
                  };
                  input.click();
                }
              }}
            >
              Selecionar fotos
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Existing Photos */}
        {photos.map((photo, index) => (
          <div key={photo.id} className="group">
            <div className="relative">
              <img
                src={photo.preview}
                alt={`Upload ${index + 1}`}
                className="w-full h-48 object-cover rounded-lg border border-gray-200"
              />
              <Button
                type="button"
                size="icon"
                variant="destructive"
                className="absolute top-2 right-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => handleDeleteClick(photo.id)}
                disabled={disabled}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-3">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Esta foto mostra
              </Label>
              <div className="flex flex-wrap gap-2">
                {availableTags.map((tag) => (
                  <Button
                    key={tag}
                    type="button"
                    variant={photo.tags?.includes(tag) ? "default" : "outline"}
                    size="sm"
                    className={cn(
                      "h-8 text-xs",
                      photo.tags?.includes(tag)
                        ? "bg-black text-white hover:bg-gray-800"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200",
                    )}
                    onClick={() => toggleTag(photo.id, tag)}
                    disabled={disabled}
                  >
                    {tag}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Upload Placeholder */}
        {canAddMore && (
          <div className="flex flex-col">
            <div
              className={cn(
                "w-full h-48 border-2 border-dashed rounded-lg bg-gray-50 flex flex-col items-center justify-center cursor-pointer transition-colors group",
                isDragOver
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 hover:bg-gray-100",
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => {
                if (!disabled) {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = "image/jpeg,image/jpg,image/png,image/webp";
                  input.multiple = true;
                  input.onchange = (e) => {
                    const target = e.target as HTMLInputElement;
                    if (target.files) {
                      handleFileUpload(target.files);
                    }
                  };
                  input.click();
                }
              }}
            >
              <div className="text-center space-y-2">
                <Plus className="mx-auto h-8 w-8 text-gray-400 group-hover:text-gray-600" />
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Arraste suas fotos aqui ou clique para enviar
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    JPEG, PNG ou WEBP até 10MB
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {photos.length > 0 && (
        <div className="text-sm text-gray-500">
          {photos.length} de {maxPhotos} fotos adicionadas
        </div>
      )}

      <Dialog
        open={deleteConfirmation.isOpen}
        onOpenChange={(open: boolean) => !open && cancelDelete()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exclusão</DialogTitle>
            <DialogDescription>
              Quer mesmo excluir esta foto?
              <br />
              Essa ação é permanente e não poderá ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={cancelDelete}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Excluir foto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
